import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import type { MaintenanceReportRow } from '../../types/safetySystem';
import { shortDate } from './SafetyUnits.columns';
import { YnChip } from './SafetyTickets.columns';
import { STATUS_TONE_CLASS, statusTone } from './components/maintenanceForm';

/**
 * Maintenance Reports grid columns — port of the V1 Safety maintenance report search
 * (mirrored by maintenance_query.py; row identity is `maintreportrecno`).
 */

const shortDateTime = (value: unknown): string => {
  const date = shortDate(value);
  if (!date) return '';
  const m = String(value).match(/[T ](\d{2}):(\d{2})/);
  return m ? `${date} ${m[1]}:${m[2]}` : date;
};

/** OPEN / SUBMITTED accent, CORRECTIONS NEEDED warning, CLOSED neutral, NOT SUBMITTED muted. */
export function StatusChip({ status, className = '' }: { status: unknown; className?: string }) {
  const s = String(status ?? '').trim().toUpperCase();
  if (!s) return null;
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-semibold leading-4 ${STATUS_TONE_CLASS[statusTone(s)]} ${className}`}>{s}</span>;
}

type Col = ColumnDef<MaintenanceReportRow>;
const dateCol = (accessorKey: string, header: string, size = 110): Col => ({ accessorKey, header, size, cell: ({ getValue }) => shortDate(getValue()) });
const dateTimeCol = (accessorKey: string, header: string, size = 140): Col => ({ accessorKey, header, size, cell: ({ getValue }) => shortDateTime(getValue()) });
const numCol = (accessorKey: string, header: string, size = 90): Col => ({ accessorKey, header, size, meta: { align: 'right' } });

export const MAINTENANCE_COLUMNS: Col[] = [
  numCol('maintreportrecno', 'Report #', 90),
  { accessorKey: 'unitnumber', header: 'Unit', size: 80 },
  { accessorKey: 'drivername', header: 'Driver', size: 180 },
  { accessorKey: 'maintmonthname', header: 'Month', size: 110 },
  numCol('maintyear', 'Year', 70),
  { accessorKey: 'maintstatus', header: 'Status', size: 150, cell: ({ getValue }) => <StatusChip status={getValue()} /> },
  numCol('truckmileage', 'Mileage', 100),
  dateCol('maintsubmitdate', 'Submitted'),
  { accessorKey: 'maintapprovedby', header: 'Approved By', size: 120 },
  dateTimeCol('maintapprovedatetime', 'Approved'),
  { accessorKey: 'maintfined', header: 'Fined', size: 70, cell: ({ getValue }) => <YnChip value={getValue()} yesTone="danger" /> },
  numCol('numdefects', 'Defects', 80),
  { accessorKey: 'maintvin', header: 'VIN', size: 170 },
  { accessorKey: 'companyrep', header: 'Company Rep', size: 130 },
  { accessorKey: 'maintdeleted', header: 'Deleted', size: 70, cell: ({ getValue }) => <YnChip value={getValue()} /> },
];

export const MAINTENANCE_DEFAULT_HIDDEN: VisibilityState = {
  maintvin: false,
  companyrep: false,
  maintdeleted: false,
};

export const MAINTENANCE_DATE_COLUMNS = ['maintsubmitdate', 'maintapprovedatetime'];
